import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Route, Clock, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useSiteImage } from "@/contexts/SiteImagesContext";

const PROFILE_LABELS = {
  cultural: { es: "Cultural", en: "Cultural", fr: "Culturel" },
  family: { es: "Familiar", en: "Family", fr: "Famille" },
  gastronomic: { es: "Gastronómico", en: "Gastronomic", fr: "Gastronomique" },
  historical: { es: "Histórico", en: "Historic", fr: "Historique" },
  romantic: { es: "Romántico", en: "Romantic", fr: "Romantique" },
  adventure: { es: "Aventura", en: "Adventure", fr: "Aventure" },
  premium: { es: "Premium", en: "Premium", fr: "Premium" },
  nocturnal: { es: "Nocturno", en: "Nightlife", fr: "Nocturne" },
  accessible: { es: "Accesible", en: "Accessible", fr: "Accessible" },
};

const TYPE_LABELS = {
  medio_dia: { es: "Medio día", en: "Half day", fr: "Demi-journée" },
  dia_completo: { es: "Día completo", en: "Full day", fr: "Journée complète" },
  nocturna: { es: "Nocturna", en: "Night", fr: "Nuit" },
};

export default function ItinerariesList() {
  const { t, lang } = useI18n();
  const itinFallback = useSiteImage("itinerary_default", "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");

  useEffect(() => {
    setLoading(true);
    const opts = {};
    if (type) opts.route_type = type;
    api.get("/itineraries", { params: opts })
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 md:py-16">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary-red))] text-white flex items-center justify-center">
          <Route className="w-6 h-6" />
        </div>
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">{t.nav.itineraries}</h1>
          <p className="text-muted-foreground flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-purple-600" /> AI-curated routes</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setType("")}
          data-testid="itin-type-all"
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${!type ? "bg-foreground text-white" : "bg-muted"}`}
        >
          {t.common.viewAll}
        </button>
        {Object.entries(TYPE_LABELS).map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setType(k)}
            data-testid={`itin-type-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              type === k ? "bg-[hsl(var(--primary-red))] text-white" : "bg-muted"
            }`}
          >
            {lbl[lang] || lbl.es}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {loading ? (
          <p className="text-muted-foreground">{t.common.loading}</p>
        ) : items.length === 0 ? (
          <div className="py-20 text-center" data-testid="itineraries-empty">
            <Route className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t.common.noResults}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="itineraries-grid">
            {items.map(it => (
              <Link
                key={it.id}
                to={`/itineraries/${it.slug}`}
                data-testid={`itinerary-card-${it.slug}`}
                className="group bg-card border border-border rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  {it.cover_image || itinFallback ? (
                    <img src={it.cover_image || itinFallback} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary-red))]/30 to-[hsl(var(--primary-green))]/20">
                      <Route className="w-16 h-16 text-white" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[hsl(var(--primary-red))] text-white text-xs font-semibold">
                    {(TYPE_LABELS[it.route_type] || {})[lang] || it.route_type}
                  </span>
                  {(PROFILE_LABELS[it.profile]) && (
                    <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white text-xs font-semibold">
                      {PROFILE_LABELS[it.profile][lang] || PROFILE_LABELS[it.profile].es}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold leading-tight tracking-tight group-hover:text-[hsl(var(--primary-red))] transition-colors line-clamp-2">{it.title}</h3>
                  {it.narrative_intro && (
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{it.narrative_intro}</p>
                  )}
                  <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {it.duration_minutes} min</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {it.points_count} stops</span>
                    {it.estimated_distance_km > 0 && <span>{it.estimated_distance_km} km</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
